<template>
    <div>
        <div v-if="errorMessage" class="alert alert-danger">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
        </div>
        
        <div v-if="emissionDate.last_number" class="alert alert-info">
            <i class="fas fa-info-circle"></i>
            <strong>Dernier document émis :</strong>
            {{ emissionDate.last_number }} du {{ emissionDate.last_at }}
        </div>

        <div class="form-group row">
            <label :for="isCredit ? 'credit_validated_at' : 'validated_at'" class="col-sm-4 col-form-label required">Date d'émission</label>
            <div class="col-sm-8">
                <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.validated_at" 
                    name="validated_at"
                    :id="isCredit ? 'credit_validated_at' : 'validated_at'" 
                    :readonly="isEmissionDateLocked"
                    required
                    @change="calculateDueDate">
                <small v-if="!isEmissionDateLocked" class="form-text text-muted">Du {{ emissionDate.min }} au {{ emissionDate.max }}.</small>
            </div>
        </div>

        <div class="form-group row" v-if="!isCredit">
            <label for="payment_delay_id" class="col-sm-4 col-form-label">Délai de paiement</label>
            <div class="col-sm-8">
                <select class="form-control" v-model="form.payment_delay_id" name="payment_delay_id" id="payment_delay_id" @change="calculateDueDate">
                    <option value="">Aucun</option>
                    <option 
                        v-for="delay in paymentDelays" 
                        :key="delay.id" 
                        :value="delay.id"
                        :data-delay="delay.delay"
                        :data-unit="delay.unit"
                        :data-is-end-of-month="delay.is_end_of_month"
                        :data-first-end-of-month="delay.first_end_of_month">
                        {{ delay.name }}
                    </option>
                </select>
            </div>
        </div>

        <div class="form-group row">
            <label for="due_at" class="col-sm-4 col-form-label required">Date d'échéance</label>
            <div class="col-sm-8">
                <input 
                    type="text" 
                    class="form-control datepicker" 
                    v-model="form.due_at" 
                    required
                    :name="isCredit ? 'expired_at' : 'due_at'"
                    :readonly="isDueDateComputed"
                    id="due_at">
                <small v-if="isDueDateComputed" class="form-text text-muted">Calculée d'après le délai de paiement.</small>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        invoiceId: {
            type: Number,
            required: true
        },
        validateUrl: {
            type: String,
            required: true
        },
        paymentDelays: {
            type: Array,
            required: true
        },
        currentPaymentDelayId: {
            type: [Number, String],
            default: null
        },
        companyPaymentDelayId: {
            type: [Number, String],
            default: null
        },
        currentDueAt: {
            type: String,
            default: null
        },
        emissionDate: {
            type: Object,
            required: true
        },
        isCredit: {
            type: Boolean,
            default: false
        }
    },

    data() {
        // Date d'émission proposée : la plus récente autorisée (le jour même)
        return {
            form: {
                validated_at: this.emissionDate.max,
                payment_delay_id: this.currentPaymentDelayId || this.companyPaymentDelayId || '',
                due_at: this.currentDueAt || this.emissionDate.max
            },
            errorMessage: null,
            DURATION_UNIT_DAYS: 1,
            DURATION_UNIT_WEEKS: 2,
            DURATION_UNIT_MONTHS: 3,
            DURATION_UNIT_YEARS: 4
        }
    },

    computed: {
        isEmissionDateLocked() {
            return this.emissionDate.min === this.emissionDate.max;
        },
        // Échéance libre seulement sans délai de paiement : sinon elle contredirait le délai affiché
        isDueDateComputed() {
            return !this.isCredit && !!this.form.payment_delay_id;
        }
    },

    watch: {
        'form.validated_at'(value) {
            $('#due_at').datepicker('setStartDate', this.parseDate(value));
        }
    },

    mounted() {
        // Bornes de la date d'émission, contrôlées aussi côté serveur
        const minDate = this.parseDate(this.emissionDate.min);
        const maxDate = this.parseDate(this.emissionDate.max);
        // Déterminer l'ID de la modal et du champ validated_at en fonction du type
        const modalId = this.isCredit ? '#validateCreditNoteModal' : '#validateInvoiceModal';
        const validatedAtId = this.isCredit ? '#credit_validated_at' : '#validated_at';
    
        // Fonction d'initialisation des datepickers
        const initDatepickers = () => {
            // Initialiser le datepicker pour validated_at entre les bornes
            $(validatedAtId).datepicker('destroy'); // Détruire l'instance existante si elle existe
            if (!this.isEmissionDateLocked) {
                $(validatedAtId).datepicker({
                    format: 'dd/mm/yyyy',
                    autoclose: true,
                    language: 'fr',
                    startDate: minDate,
                    endDate: maxDate
                });
            }
            
            // Initialiser le datepicker pour due_at
            $('#due_at').datepicker('destroy');
            $('#due_at').datepicker({
                format: 'dd/mm/yyyy',
                autoclose: true,
                language: 'fr',
                startDate: this.parseDate(this.form.validated_at),
                enableOnReadonly: false
            });

            // Configurer le calcul automatique de due_at
            $(validatedAtId).off('changeDate').on('changeDate', (e) => {
                this.form.validated_at = e.format('dd/mm/yyyy');
                
                if(!this.isCredit){
                    // Pour les factures : calcul basé sur le délai de paiement
                    this.calculateDueDate();
                } else {
                    // Pour les avoirs : date de validation + 30 jours
                    const validatedDate = this.parseDate(this.form.validated_at);
                    if (validatedDate) {
                        const expiredDate = new Date(validatedDate);
                        expiredDate.setDate(expiredDate.getDate() + 30);
                        this.form.due_at = this.formatDate(expiredDate);
                    }
                }
            });
            
            this.$nextTick(() => {
                if(!this.isCredit){
                    this.calculateDueDate();
                } else {
                    // Pour les avoirs : initialiser avec date de validation + 30 jours ou currentDueAt
                    if(this.currentDueAt) {
                        this.form.due_at = this.currentDueAt;
                    } else if(this.form.validated_at) {
                        const validatedDate = this.parseDate(this.form.validated_at);
                        if (validatedDate) {
                            const expiredDate = new Date(validatedDate);
                            expiredDate.setDate(expiredDate.getDate() + 30);
                            this.form.due_at = this.formatDate(expiredDate);
                        }
                    }
                }
            });
        };
        
        // Initialiser les datepickers à l'ouverture de la modal (show ou credit)
        $(modalId).on('shown.bs.modal', initDatepickers);
        
        // Initialiser les datepickers à l'ouverture de la modal bulk factures
        $('#bulkSyncModal').on('shown.bs.modal', initDatepickers);
        
        // Initialiser les datepickers à l'ouverture de la modal bulk avoirs
        $('#bulkFinalizeCreditNoteModal').on('shown.bs.modal', initDatepickers);
    },

    methods: {
        formatDate(date) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        },

        parseDate(dateStr) {
            const parts = dateStr.split('/');
            if (parts.length !== 3) return null;
            return new Date(parts[2], parts[1] - 1, parts[0]);
        },

        calculateDueDate() {
            if (!this.form.validated_at) {
                return;
            }

            if (!this.form.payment_delay_id) {
                // Si pas de délai de paiement, due_at = validated_at
                this.form.due_at = this.form.validated_at;
                return;
            }

            // Trouver le délai de paiement sélectionné
            const selectedDelay = this.paymentDelays.find(d => d.id == this.form.payment_delay_id);
            if (!selectedDelay) {
                this.form.due_at = this.form.validated_at;
                return;
            }

            // Parser la date de validation
            let date = this.parseDate(this.form.validated_at);
            if (!date) {
                this.form.due_at = this.form.validated_at;
                return;
            }

            // Si fin de mois AVANT l'ajout du délai
            if (selectedDelay.is_end_of_month && selectedDelay.first_end_of_month) {
                date = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            }

            // Ajouter le délai selon l'unité
            switch (selectedDelay.unit) {
                case this.DURATION_UNIT_WEEKS:
                    date.setDate(date.getDate() + (selectedDelay.delay * 7));
                    break;
                case this.DURATION_UNIT_MONTHS:
                    date.setMonth(date.getMonth() + selectedDelay.delay);
                    break;
                case this.DURATION_UNIT_YEARS:
                    date.setFullYear(date.getFullYear() + selectedDelay.delay);
                    break;
                default: // DURATION_UNIT_DAYS
                    date.setDate(date.getDate() + selectedDelay.delay);
            }

            // Si fin de mois APRÈS l'ajout du délai
            if (selectedDelay.is_end_of_month && !selectedDelay.first_end_of_month) {
                date = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            }

            this.form.due_at = this.formatDate(date);
        },
    }
}
</script>
